import { enhanceGLBridge, imageKit, maskSmearBridge, type MTIKManager } from '@meitu/wasm-lib';
import {
    EdgelessObject,
    type IFramework,
    type IGetSrcImage,
    type IResultOptions,
    type ISetResource,
    type TBBox,
} from '@/framework';
import { loadImageAsync } from '@/utils/file-kit';
import { glKit } from '@/utils/gl-kit';
import { getMainLayerRenderSize } from '@/utils/layer-kit';
import { LayerType, type IImageLayerProps } from '@/version-types';
import { renderMainLayerCanvas } from './render/render-to-canvas';

export interface IMainCanvasResultOptions {
    aiWatermark?: boolean;
    forceRerender?: boolean;
}

export class EdgelessMainLayer extends EdgelessObject implements IGetSrcImage, ISetResource {
    static type = LayerType.MAIN_LAYER;

    constructor(layerId: string, framework: IFramework) {
        super(layerId, framework.service);
    }

    async getSrcImage() {
        const layer = this.service.flatLayer.getLayer(this.layerId);
        const data = layer.data as IImageLayerProps;
        return loadImageAsync(data.url);
    }

    /**
     * 更新主图临时资源并刷新所属画布缓存
     *
     * 副作用：标记所属画布为脏并触发重绘。
     *
     * @param baseImage - 滤镜预览或恢复后的源图
     */
    setResource(baseImage: HTMLImageElement | HTMLCanvasElement) {
        const layer = this.service.flatLayer.getLayer(this.layerId);
        this.resource = renderMainLayerCanvas({ layer, baseImage });
        this.service.edgelessCanvas.setDirty({ object: this });
    }

    private async getResource(forceRerender = false) {
        const layer = this.service.flatLayer.getLayer(this.layerId);

        let baseImage: HTMLImageElement | HTMLCanvasElement;
        let manager: MTIKManager | null = null;

        if (glKit.isUseGL(layer)) {
            // console.log('===>getResource gl ', { layer });
            // 设置强制重新渲染，隐藏保护区（例如：下载）
            if (forceRerender) {
                maskSmearBridge.enableRenderToView(false, false);
                manager = await enhanceGLBridge.getManager();
                manager.processRender(true);
            }
            baseImage = imageKit.getGLCanvas() as HTMLCanvasElement;
        } else {
            // console.log('===>getResource img', { layer });
            baseImage = await this.getSrcImage();
        }

        const canvas = renderMainLayerCanvas({ layer, baseImage, forceRerender });

        // 恢复保护区
        if (manager) {
            maskSmearBridge.enableRenderToView(true, false);
            manager?.processRender(true);
        }

        return canvas;
    }

    async getCanvasResult({ aiWatermark = false, forceRerender = false }: IMainCanvasResultOptions = {}) {
        const canvas = await this.getResource(forceRerender);
        if (aiWatermark) {
            await super.drawAiWatermarkAsync(canvas);
        }
        return canvas;
    }

    override supportsCanvasResult() {
        return true;
    }

    override async getResult({ aiWatermark = false }: IResultOptions = {}) {
        const renderBox = this.getBaseInfo();
        const canvas = await this.getResource();
        if (aiWatermark) {
            await super.drawAiWatermarkAsync(canvas);
        }
        return { canvas, renderBox };
    }

    override async loadResource() {
        const canvas = await this.getResource();
        await super.drawAiWatermarkAsync(canvas);
        return canvas;
    }

    override getBaseInfo(): TBBox {
        const layer = this.service.flatLayer.getLayer(this.layerId);
        const size = getMainLayerRenderSize(layer);
        const position = this.calcCenterIsLeftTopPosition({
            left: layer.left,
            top: layer.top,
            rotate: layer.rotate,
            ...size,
        });

        return {
            ...position,
            ...size,
        };
    }
}
